import { useEffect, useState } from "react";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import Button from "../../../components/shared/buttons/button";
import {
  useCreateReworkEmployeeDataMutation,
  useUpdateReworkEmployeeDataMutation,
} from "./reworkApi";
import useTranslator from "../../../hooks/useTranslator";
import { PlusSvg } from "../../../svgFiles/PlusSvg";

const MAX_EMPLOYEES = 15;

const emptyRow = () => ({
  employee_name: "",
  kg_before_lunch: "",
  kg_after_lunch: "",
  boxes_before_lunch: "",
  boxes_after_lunch: "",
});

const calcTotals = (row) => ({
  total_kg: Number(row.kg_before_lunch || 0) + Number(row.kg_after_lunch || 0),
  total_boxes: Number(row.boxes_before_lunch || 0) + Number(row.boxes_after_lunch || 0),
});

const AddEmployeeModal = ({ show, setShow, record, mode, lotNumber }) => {
  const { _t } = useTranslator();
  const [createEmployee, { isLoading: isCreating }] = useCreateReworkEmployeeDataMutation();
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateReworkEmployeeDataMutation();

  // For add mode: array of rows; for edit mode: single row object
  const [rows, setRows] = useState([emptyRow()]);
  const [editData, setEditData] = useState(emptyRow());

  useEffect(() => {
    if (mode === "edit" && record) {
      setEditData({
        employee_name: record.employee_name || "",
        kg_before_lunch: record.kg_before_lunch || "",
        kg_after_lunch: record.kg_after_lunch || "",
        boxes_before_lunch: record.boxes_before_lunch || "",
        boxes_after_lunch: record.boxes_after_lunch || "",
      });
    } else {
      setRows([emptyRow()]);
    }
  }, [mode, record, show]);

  const handleClose = () => setShow(false);

  // Add mode handlers
  const handleAddRow = () => {
    if (rows.length < MAX_EMPLOYEES) {
      setRows([...rows, emptyRow()]);
    }
  };

  const handleRemoveRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, e) => {
    const { name, value } = e.target;
    const updated = rows.map((row, i) => i === index ? { ...row, [name]: value } : row);
    setRows(updated);
  };

  // Edit mode handler
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating || isUpdating) return;

    try {
      if (mode === "edit") {
        const totals = calcTotals(editData);
        await updateEmployee({
          id: record.id,
          requestBody: {
            ...editData,
            ...totals,
            lot_number_id: lotNumber,
          },
        }).unwrap();
      } else {
        const employees = rows.map((row) => ({
          ...row,
          ...calcTotals(row),
          lot_number_id: lotNumber,
        }));
        await createEmployee({ employees }).unwrap();
      }
      handleClose();
    } catch (error) {
      console.error("Error saving employee data:", error);
    }
  };

  const renderRow = (row, index, onChange, isEdit = false) => {
    const { total_kg, total_boxes } = calcTotals(row);
    return (
      <div key={index} className="mb-4 p-2 border rounded" style={{ background: "rgba(238, 114, 37, 0.04)", borderLeft: "3px solid #ee7225" }}>
        <Row className="g align-items-end position-relative">
          <Col lg={4}>
            <InputWithLabel
              label={<span><span className="text-muted me-1" style={{ fontSize: "12px" }}>#{index + 1}</span>{_t("employee_name", "Employee Name")}</span>}
              type="text"
              name="employee_name"
              value={row.employee_name}
              onChange={onChange}
              placeholder={_t("employee_name_placeholder", "Name...")}
              disabled={isEdit}
            />
          </Col>
          <Col lg={2}>
            <InputWithLabel label={_t("kg_before_lunch", "KG Before")} type="number" name="kg_before_lunch" value={row.kg_before_lunch} onChange={onChange} placeholder="0" />
          </Col>
          <Col lg={2}>
            <InputWithLabel label={_t("kg_after_lunch", "KG After")} type="number" name="kg_after_lunch" value={row.kg_after_lunch} onChange={onChange} placeholder="0" />
          </Col>
          <Col lg={2}>
            <InputWithLabel label={_t("boxes_before_lunch", "Boxes Before")} type="number" name="boxes_before_lunch" value={row.boxes_before_lunch} onChange={onChange} placeholder="0" />
          </Col>
          <Col lg={2}>
            <InputWithLabel label={_t("boxes_after_lunch", "Boxes After")} type="number" name="boxes_after_lunch" value={row.boxes_after_lunch} onChange={onChange} placeholder="0" />
          </Col>
          {!isEdit && rows.length > 1 && (

            <span
              onClick={() => handleRemoveRow(index)}
              title={_t("remove", "Remove")}
              style={{
                cursor: "pointer",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: "#ee7225",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                flexShrink: 0,
                top: '-18px',
                right: '-9px',
                fontSize:"10px"
              }}
              className="position-absolute p-0"
            >
              ✕
            </span>

          )}
        </Row>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <small className="text-muted">
            {_t("total_kg", "Total KG")}: <strong>{total_kg}</strong> &nbsp;|&nbsp;
            {_t("total_boxes", "Total Boxes")}: <strong>{total_boxes}</strong>
          </small>
        </div>
      </div>
    );
  };

  const renderEditFields = () =>
    renderRow(editData, 0, handleEditChange, true);

  const renderAddFields = () => (
    <>
      {rows.map((row, index) =>
        renderRow(row, index, (e) => handleRowChange(index, e))
      )}
      {rows.length < MAX_EMPLOYEES && (
        <Button
          size="small"
          label={_t("add_another_employee", "Add Another Employee")}
          icon={PlusSvg}
          onClick={handleAddRow}
          className="borderRedBtn mb-2"
        />
      )}
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t("add_employee_log_data", "Add Employee Log Data")
          : _t("edit_employee_log_data", "Edit Employee Log Data")
      }
      children={mode === "edit" ? editData : rows}
    >
      {mode === "edit" ? renderEditFields() : renderAddFields()}

      <Row>
        <Col className="text-end">
          <Button
            label={_t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            disabled={isCreating || isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default AddEmployeeModal;
